import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Close, Send } from '@mui/icons-material';
import {
  markCheckinMessagesRead,
  sendCheckinMessage,
  useCheckinMessages,
} from './services';

interface Props {
  open: boolean;
  onClose: () => void;
  childId: string;
  familyId: string;
  childName: string;
  role: 'admin' | 'parent';
  senderId: string;
  senderName: string;
}

const ChatWindow: React.FC<Props> = ({
  open,
  onClose,
  childId,
  familyId,
  childName,
  role,
  senderId,
  senderName,
}) => {
  const messages = useCheckinMessages(open ? childId : '', open ? familyId : '');
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open || !childId || !familyId) return;
    markCheckinMessagesRead(childId, familyId, role);
  }, [open, messages.length, childId, familyId, role]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    const trimmed = text.trim();
    if (!trimmed || sending) return;
    setSending(true);
    try {
      await sendCheckinMessage({
        childId,
        familyId,
        senderId,
        senderName,
        senderRole: role,
        text: trimmed,
        readByAdmin: role === 'admin',
        readByParent: role === 'parent',
      });
      setText('');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 1,
        }}
      >
        <Typography fontWeight="bold">Messages — {childName}</Typography>
        <IconButton onClick={onClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{ p: 0, display: 'flex', flexDirection: 'column', height: 420 }}
      >
        <Box sx={{ flex: 1, overflowY: 'auto', px: 2, py: 1 }}>
          {messages.length === 0 && (
            <Typography
              variant="body2"
              color="text.secondary"
              textAlign="center"
              sx={{ mt: 6 }}
            >
              No messages yet. Send one to get started.
            </Typography>
          )}

          {messages.map((msg) => {
            const isOwn = msg.senderId === senderId;
            return (
              <Stack
                key={msg.id}
                direction="row"
                justifyContent={isOwn ? 'flex-end' : 'flex-start'}
                sx={{ mb: 1 }}
              >
                <Box
                  sx={{
                    maxWidth: '75%',
                    bgcolor: isOwn ? 'primary.main' : 'grey.100',
                    color: isOwn ? 'white' : 'text.primary',
                    borderRadius: isOwn
                      ? '16px 16px 4px 16px'
                      : '16px 16px 16px 4px',
                    px: 1.5,
                    py: 0.75,
                  }}
                >
                  {!isOwn && (
                    <Typography
                      variant="caption"
                      fontWeight={600}
                      display="block"
                      color="text.secondary"
                    >
                      {msg.senderName}
                    </Typography>
                  )}
                  <Typography variant="body2">{msg.text}</Typography>
                  <Typography
                    variant="caption"
                    display="block"
                    textAlign="right"
                    sx={{ opacity: 0.65, mt: 0.25 }}
                  >
                    {msg.createdAt.toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </Typography>
                </Box>
              </Stack>
            );
          })}
          <div ref={bottomRef} />
        </Box>

        <Box
          sx={{
            px: 2,
            py: 1.5,
            borderTop: '1px solid',
            borderColor: 'divider',
          }}
        >
          <TextField
            fullWidth
            size="small"
            placeholder="Type a message…"
            value={text}
            onChange={(e) => setText(e.target.value.slice(0, 1000))}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
            multiline
            maxRows={3}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleSend}
                    disabled={!text.trim() || sending}
                    size="small"
                  >
                    {sending ? <CircularProgress size={18} /> : <Send />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ChatWindow;
